import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import SharedItinerary from './SharedItinerary';
import { WEB_APP_URL } from '../../utils/webApp';
import './TripLinkFallbackPage.css';

const API_BASE =
  process.env.REACT_APP_APP_BASE_URL ||
  (typeof window !== 'undefined' && window.location.origin.includes('localhost')
    ? 'https://app.voiatravel.com'
    : '');

const TripLinkFallbackPage = () => {
  const { tripId } = useParams();
  const { state } = useLocation();
  // ?s=<token>: a shared itinerary link. The full plan renders here, no install needed.
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('s');
  const shareToken = rawToken && /^[0-9a-f]{16,64}$/i.test(rawToken) ? rawToken : null;
  const [share, setShare] = useState(null);
  const [shareStatus, setShareStatus] = useState(null);
  const [trip, setTrip] = useState(() => ({
    name: state?.name ?? null,
    description: state?.description ?? null,
    imageUrl: state?.imageUrl ?? null,
    creatorName: state?.creatorName ?? null,
  }));
  const [loading, setLoading] = useState(!!shareToken || (!state?.name && !state?.imageUrl));
  const appStoreUrl = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB';
  const playStoreUrl = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';

  useEffect(() => {
    if (!shareToken && (state?.name != null || state?.imageUrl != null)) return;
    if (!tripId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const query = shareToken ? `?s=${shareToken}` : '';
    fetch(`${API_BASE}/api/trip/${encodeURIComponent(tripId)}${query}`)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (!cancelled) {
          setTrip({
            name: data.name || null,
            description: data.description || null,
            imageUrl: data.imageUrl || null,
            creatorName: data.creatorName || null,
          });
          if (shareToken) {
            setShare(data.share && data.share.ok ? data.share : null);
            setShareStatus(data.shareStatus || null);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tripId, shareToken, state?.name, state?.imageUrl]);

  if (!loading && share && shareToken) {
    return <SharedItinerary tripId={tripId} token={shareToken} share={share} />;
  }
  const shareDead = !!shareToken && !loading && !share;

  const headingText = trip.name || 'View trip in VOIA';
  const subtitle = shareDead
    ? (shareStatus === 'unavailable'
      ? 'We could not load this itinerary right now. Please try again in a moment.'
      : 'This link is no longer active. Ask the person who shared it to send you a new one.')
    : trip.name
      ? 'See the full trip and start planning your own.'
      : 'This link opens a trip on VOIA.';
  const ctaLine = (appStoreUrl || playStoreUrl)
    ? 'Install VOIA to view this trip'
    : null;
  // Desktop: the web app opens the trip after sign-in (members see it all); phones get the stores.
  const webTripUrl = `${WEB_APP_URL}/view-trip?tripId=${encodeURIComponent(tripId)}`;

  return (
    <div className="trip-fallback-container">
      <main className="trip-fallback-content">
        {loading ? (
          <p className="trip-fallback-description">Loading…</p>
        ) : (
          <>
            <header className="trip-fallback-header">
              {trip.imageUrl && (
                <div className="trip-fallback-image-wrap">
                  <img
                    src={trip.imageUrl}
                    alt={trip.name || 'Trip'}
                    className="trip-fallback-image"
                  />
                </div>
              )}
              <h1 className="trip-fallback-title">{headingText}</h1>
              {trip.creatorName && (
                <p className="trip-fallback-creator">by {trip.creatorName}</p>
              )}
            </header>

            <div className="trip-fallback-cta-block">
              <p className="trip-fallback-description">{subtitle}</p>
              {ctaLine && (
                <p className="trip-fallback-cta onlyTouch">{ctaLine}</p>
              )}
              <img
                src={`${process.env.PUBLIC_URL || ''}/og-preview.png`}
                alt="Voia"
                className="trip-fallback-og-preview"
              />
            </div>

            {!shareDead && (
              <div className="onlyDesktop" data-track-location="trip_page">
                <a href={webTripUrl} className="webAppButton">
                  Log in to open this trip
                </a>
                <p className="webAppOr">Or get the app</p>
              </div>
            )}

            <div className="trip-fallback-stores">
              <a
                href={appStoreUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="trip-fallback-store-link"
                aria-label="Download on the App Store"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/app_store_badge.svg`}
                  alt="Download on the App Store"
                  className="trip-fallback-store-badge"
                />
              </a>
              <a
                href={playStoreUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="trip-fallback-store-link"
                aria-label="Get it on Google Play"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/google_play_badge.png`}
                  alt="Get it on Google Play"
                  className="trip-fallback-store-badge"
                />
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default TripLinkFallbackPage;
